import type { APIResponse } from "@chinese-vendor/schemas";
import type { SupportedLocale } from "@chinese-vendor/i18n";
import { localizationSyncService } from "../i18n/localizationSync";
import { Platform } from "react-native";

const API_VERSION_PREFIX = "/api/v1";

export function normalizeApiBaseUrl(rawBaseUrl: string): string {
  const trimmedBaseUrl = rawBaseUrl.trim().replace(/\/+$/, "");

  if (trimmedBaseUrl === "") {
    throw new Error("API base URL must not be empty or whitespace");
  }

  if (trimmedBaseUrl.endsWith(API_VERSION_PREFIX)) {
    return trimmedBaseUrl;
  }

  return `${trimmedBaseUrl}${API_VERSION_PREFIX}`;
}

const API_BASE_URL: string = normalizeApiBaseUrl(
  (process.env.EXPO_PUBLIC_API_BASE_URL as string | undefined) ?? "http://localhost:8000"
);

// ─── Token accessor ───────────────────────────────────────────────────────────
// Injected at runtime by AuthContext; avoids circular imports.
let _getToken: (() => string | null) | null = null;

export function registerTokenAccessor(fn: () => string | null): void {
  _getToken = fn;
}

// ─── Locale accessor ──────────────────────────────────────────────────────────
// Injected at runtime by I18nContext so the sync service knows which locale's
// bundle to fetch. Falls back to 'en' if not yet registered.
let _getLocale: (() => SupportedLocale) | null = null;

export function registerLocaleAccessor(fn: () => SupportedLocale): void {
  _getLocale = fn;
}

// ─── API Error ────────────────────────────────────────────────────────────────

export class APIError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly errorCode?: string
  ) {
    super(message);
    this.name = "APIError";
  }
}

// ─── Core fetch wrapper ───────────────────────────────────────────────────────

async function request<T>(path: string, options: RequestInit = {}): Promise<APIResponse<T>> {
  const token = _getToken?.();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
  });

  const json = (await response.json()) as APIResponse<T>;

  if (!response.ok || json.status === "error") {
    throw new APIError(response.status, json.error ?? `HTTP ${response.status}`);
  }

  // ── Localization hash interception ─────────────────────────────────────────
  // Fire-and-forget: if the API signals a newer dictionary version, fetch it.
  if (json.localization_hash) {
    const locale = _getLocale?.() ?? "en";
    localizationSyncService.checkAndSync(json.localization_hash, locale).catch(() => {});
  }

  return json;
}

// ─── Public helpers ───────────────────────────────────────────────────────────

export const apiClient = {
  get<T>(path: string): Promise<APIResponse<T>> {
    return request<T>(path, { method: "GET" });
  },

  post<T>(path: string, body: unknown): Promise<APIResponse<T>> {
    return request<T>(path, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  put<T>(path: string, body: unknown): Promise<APIResponse<T>> {
    return request<T>(path, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  patch<T>(path: string, body: unknown): Promise<APIResponse<T>> {
    return request<T>(path, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  delete<T>(path: string, body?: unknown): Promise<APIResponse<T>> {
    return request<T>(path, {
      method: "DELETE",
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  },

  /** Upload a file to the backend (which handles S3 streaming per SPEC §2.1.2.4) */
  async upload<T>(
    path: string,
    file: { uri: string; name: string; type: string },
    onProgress?: (progress: number) => void
  ): Promise<APIResponse<T>> {
    const token = _getToken?.();
    const formData = new FormData();

    if (Platform.OS === "web") {
      // On web, file.uri is a blob: URL from URL.createObjectURL().
      // The React Native { uri, name, type } trick does NOT work on web —
      // FormData serialises plain objects as "[object Object]".
      // Fetch the blob URL to get the real Blob, then append it.
      try {
        const blobResponse = await fetch(file.uri);
        if (!blobResponse.ok) {
          throw new APIError(blobResponse.status, "Failed to read file from blob URL");
        }
        const blob = await blobResponse.blob();
        formData.append("file", blob, file.name);
      } catch (err) {
        if (err instanceof APIError) throw err;
        throw new APIError(0, "Unexpected error while preparing file upload");
      }
    } else {
      // React Native FormData accepts { uri, name, type } as a file entry.
      formData.append("file", { uri: file.uri, name: file.name, type: file.type } as unknown as Blob);
    }

    // TODO: Replace XHR with a fetch-based upload once streaming progress
    // is natively supported in React Native.
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API_BASE_URL}${path}`);
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);

      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress(e.loaded / e.total);
        };
      }

      xhr.onload = () => {
        try {
          const json = JSON.parse(xhr.responseText) as APIResponse<T>;
          if (json.status === "error") {
            reject(new APIError(xhr.status, json.error ?? "Upload failed"));
          } else {
            resolve(json);
          }
        } catch {
          reject(new APIError(xhr.status, "Failed to parse upload response"));
        }
      };

      xhr.onerror = () => reject(new APIError(0, "Network error during upload"));
      xhr.send(formData);
    });
  },
};
